import * as React from 'react'
import {Slot} from '@radix-ui/react-slot'
import {cva, type VariantProps} from 'class-variance-authority'
import {cn} from '@/lib/utils'
import {Link as SPALink} from 'react-router-dom'
import {HashLink} from 'react-router-hash-link'

export const routeTypes = {
    SFRA: 'sfra',
    INTERNAL: 'internal',
    EXTERNAL: 'external',
    ELEMENT: 'element'
} as const

export type RouteType = (typeof routeTypes)[keyof typeof routeTypes]

const ctaVariants = cva('transition-all duration-[0.15s] ease-in-out', {
    variants: {
        ctaType: {
            link: 'text-[0.875rem]/[1.375rem] tracking-normal text-emphasis',
            button: 'inline-flex justify-center items-center h-[3.125rem] font-grotesque font-medium text-base tracking-normal focus:shadow-[0_0_5px_0_rgb(0_0_0_/_0.1)] focus:outline-none rounded-[0.5rem] py-[12px] px-[18px]'
        },
        variant: {
            breadcrumbLink: 'font-grotesque font-light hover:no-underline underline',
            normal: 'font-grotesque font-light',
            bold: 'font-grotesque font-medium hover:text-emphasis/90',
            navLink:
                'block font-grotesque font-light text-blue-05 leading-normal [&_.builder-text]:leading-normal font-light pt-[0.625rem] pr-[0.5rem] pb-[0.4375rem] pl-[1.25rem] hover:bg-accent hover:text-white hover:rounded-lg hover:no-underline',
            primary:
                'bg-primary hover:bg-primary-hover text-primary-foreground focus:shadow-primary-focus-shadow',
            secondary:
                'bg-secondary text-secondary-foreground hover:text-secondary-hover-text border-[1px] border-solid border-foreground focus:shadow-secondary-focus-shadow'
        }
    },
    defaultVariants: {
        ctaType: 'link',
        variant: 'normal'
    }
})

interface CommonButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof ctaVariants> {
    asChild?: boolean
    routeType?: RouteType
    hrefValue?: string
    openInNewTab?: boolean
    children: React.ReactNode
    'data-testid'?: string | undefined
}

interface ButtonProps extends CommonButtonProps {
    hrefValue?: string
    routeType?: RouteType
    openInNewTab?: boolean
    'data-testid'?: string | undefined
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            ctaType,
            variant,
            disabled,
            openInNewTab = false,
            asChild = false,
            routeType,
            hrefValue,
            children,
            'data-testid': dataTestId,
            ...props
        },
        ref
    ) => {
        const buttonChildren = React.useMemo(() => {
            const linkProps = {
                ...(openInNewTab && {target: '_blank'}),
                ...(openInNewTab || routeType === routeTypes.EXTERNAL
                    ? {rel: 'noopener noreferrer'}
                    : {})
            }

            const buttonChildrenClassnames =
                !asChild && 'flex items-center justify-center w-full h-full'

            if (!hrefValue && routeType !== routeTypes.EXTERNAL) {
                return <div className={cn(buttonChildrenClassnames)}>{children}</div>
            } else if (routeType === routeTypes.SFRA) {
                return (
                    <a href={hrefValue} className={cn(buttonChildrenClassnames)} {...linkProps}>
                        {children}
                    </a>
                )
            } else if (routeType === routeTypes.INTERNAL) {
                return (
                    <SPALink
                        to={hrefValue}
                        className={cn(buttonChildrenClassnames)}
                        data-testid={dataTestId || 'button-SPALink'}
                    >
                        {children}
                    </SPALink>
                )
            } else if (routeType === routeTypes.ELEMENT) {
                return (
                    <HashLink
                        smooth
                        to={hrefValue}
                        className={cn(buttonChildrenClassnames)}
                        data-testid={dataTestId || 'button-HashLink'}
                    >
                        {children}
                    </HashLink>
                )
            } else {
                return (
                    <a href={hrefValue} className={cn(buttonChildrenClassnames)} {...linkProps}>
                        {children}
                    </a>
                )
            }
        }, [routeType, hrefValue, children, openInNewTab, asChild, dataTestId])

        const Comp = asChild ? Slot : 'button'
        const variantClassNames = cn(
            ctaVariants({ctaType, variant, className}),
            {
                'text-disabled pointer-events-none': ctaType === 'link' && disabled,
                'no-underline pointer-events-none text-emphasis':
                    ctaType === 'link' && variant === 'breadcrumbLink' && disabled,
                'bg-disabled pointer-events-none':
                    ctaType === 'button' && variant === 'primary' && disabled,
                'text-disabled border-disabled pointer-events-none':
                    ctaType === 'button' && variant === 'secondary' && disabled
            },
            className
        )

        return (
            <Comp
                className={variantClassNames}
                ref={ref}
                {...props}
                data-testid={dataTestId || 'button-Comp'}
            >
                {buttonChildren}
            </Comp>
        )
    }
)
Button.displayName = 'Button'

export {Button, ctaVariants}
